import { isBefore, isSameDay } from "date-fns";
import { ROLES } from "../../constants/base";

// Pure logic behind the Home "Prossime operazioni" table: which operations, in which
// order, how urgent they are and which lots/tanks they touch.

// UX brief: at most 5 rows on the Home, the rest behind "Vedi tutte".
export const MAX_NEXT_OPERATIONS = 5;

const DAY_MS = 24 * 60 * 60 * 1000;

// These keep running after their date once started, so they are not "late" in the usual sense.
const LONG_RUNNING_TYPES = ["STABILIZATION_STATIC", "STABILIZATION_DYNAMIC", "STABILIZATION_RESIN", "FILTERING"];

export const isOwnOperation = (operation, userId) =>
  Array.isArray(operation?.cellarman_ids) &&
  operation.cellarman_ids.some((id) => Number(id) === Number(userId));

// Expired operations first, then upcoming ones; by expiry, then priority (high first), then id.
// The limit applies AFTER the sort, so the rows kept are the most urgent ones.
export const pickNextOperations = ({
  operations,
  expiredOperations,
  role,
  userId,
  now = new Date(),
  limit = MAX_NEXT_OPERATIONS,
}) => {
  const onlyOwn = (list) =>
    Number(role) === ROLES.USER ? (list || []).filter((o) => isOwnOperation(o, userId)) : list || [];

  const upcoming = onlyOwn(operations).filter((o) => now <= new Date(o?.expire_date));

  return [...onlyOwn(expiredOperations), ...upcoming]
    .sort((a, b) => {
      const byDate = new Date(a?.expire_date) - new Date(b?.expire_date);
      if (byDate !== 0) return byDate;
      const byPriority = (b?.priority ?? 0) - (a?.priority ?? 0);
      if (byPriority !== 0) return byPriority;
      return (a?.id ?? 0) - (b?.id ?? 0);
    })
    .slice(0, limit);
};

export const getOperationUrgency = (operation, now = new Date()) => {
  const expire = new Date(operation?.expire_date);
  const isOverdue = isBefore(expire, now);
  const needsWarning =
    isOverdue && (!LONG_RUNNING_TYPES.includes(operation?.type) || !operation?.start_date);
  return {
    isOverdue,
    needsWarning,
    daysOverdue: needsWarning ? Math.floor((now - expire) / DAY_MS) : 0,
  };
};

// One status per row: overdue | inProgress | toSample | sampled | today | scheduled.
export const getOperationStatus = (operation, now = new Date()) => {
  if (getOperationUrgency(operation, now).needsWarning) return "overdue";
  if (LONG_RUNNING_TYPES.includes(operation?.type) && operation?.start_date) return "inProgress";
  if (operation?.type === "ANALYSIS") return operation?.sampled ? "sampled" : "toSample";
  if (isSameDay(new Date(operation?.expire_date), now)) return "today";
  return "scheduled";
};

const uniq = (values) => [...new Set(values.filter(Boolean))];

// Names of the lots and tanks an operation touches. Tank names come from the loaded tanks
// (needs TANKS read); without them they are omitted rather than guessed. Lot names come from
// the loaded lots, falling back to the batch name the operation itself carries.
export const describeOperationTargets = (operation, { tanks = [], lots = [] } = {}) => {
  const entries = [
    ...(Array.isArray(operation?.src_tanks) ? operation.src_tanks : []),
    ...(Array.isArray(operation?.dest_tanks) ? operation.dest_tanks : []),
  ];

  const tankName = (id) => (tanks || []).find((t) => Number(t?.id) === Number(id))?.name;
  const lotName = (entry) =>
    (lots || []).find((l) => Number(l?.id) === Number(entry?.batch_id))?.name ||
    entry?.batch_name ||
    entry?.old_batch_name;

  return {
    lots: uniq(entries.map(lotName)),
    tanks: uniq(entries.filter((e) => e?.tank_id != null).map((e) => tankName(e.tank_id))),
  };
};
